import { Component, OnInit, inject, ChangeDetectorRef, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormControl } from '@angular/forms';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { AppDropdown } from '../../../../shared/components/dropdown/dropdown';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../../../core/services/settings.service';
import { Store } from '@ngrx/store';
import { selectBranchId } from '../../../../core/store/auth/auth.selectors';
import { SpecialityModalComponent } from './modals/speciality-modal/speciality-modal';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { toast } from 'ngx-sonner';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';

/**
 * Value of the group filter dropdown. Strings are the fixed choices, a number
 * is the id of one specific group (show its members).
 */
type GroupFilterValue = 'all' | 'groups' | 'ungrouped' | number;

@Component({
  selector: 'app-speciality-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    AppPrimeTable,
    AppDropdown,
    AppButton,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    DialogModule,
    SelectModule,
    TranslateModule,
    SpecialityModalComponent,
    HasPermissionDirective
  ],
  templateUrl: './speciality-tab.html'
})
export class SpecialityTab implements OnInit {
  private settingsService = inject(SettingsService);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private perm = inject(PermissionService);

  get canCreate(): boolean { return this.perm.canCreate(MODULE.CONFIGURACOES); }
  get canEdit(): boolean { return this.perm.canEdit(MODULE.CONFIGURACOES); }
  get canDelete(): boolean { return this.perm.canDelete(MODULE.CONFIGURACOES); }

  @ViewChild('nameTpl', { static: true }) nameTpl!: TemplateRef<any>;
  @ViewChild('groupTpl', { static: true }) groupTpl!: TemplateRef<any>;
  @ViewChild('actionsTpl', { static: true }) actionsTpl!: TemplateRef<any>;
  @ViewChild('statusTpl', { static: true }) statusTpl!: TemplateRef<any>;

  specialties: any[] = [];
  /** All groups of the branch: feeds the filter dropdown and the group pickers. */
  groups: any[] = [];
  /** group id -> number of member specialities, for the badge on group rows. */
  memberCounts: Record<number, number> = {};
  loading = true;
  searchControl = new FormControl('');
  groupFilterControl = new FormControl<GroupFilterValue>('all', { nonNullable: true });
  branchId: number | null = null;

  // Pagination
  totalRecords = 0;
  rows = 10;
  first = 0;

  // Add/Edit modal (speciality or group)
  showSpecialityModal = false;
  selectedSpeciality: any = null;
  modalIsGroup = false;

  // "Add to group" modal
  showAssignModal = false;
  specialityToAssign: any = null;
  assignGroupId: number | null = null;
  assigning = false;

  // Delete modal
  showDeleteModal = false;
  specialityToDelete: any = null;

  templates: { [key: string]: TemplateRef<any> } = {};

  columns: PrimeTableColumn[] = [
    { field: 'name', header: 'speciality', width: '40%' },
    { field: 'group', header: 'speciality_group', width: '25%' },
    { field: 'visibility', header: 'status', width: '25%' },
    { field: 'actions', header: 'actions', type: 'action', width: '10%' }
  ];

  get groupFilterOptions(): { label: string; value: GroupFilterValue; isGroup?: boolean }[] {
    return [
      { label: this.translate.instant('speciality_group_filter_all'), value: 'all' },
      { label: this.translate.instant('speciality_group_filter_groups'), value: 'groups' },
      { label: this.translate.instant('speciality_group_filter_ungrouped'), value: 'ungrouped' },
      ...this.groups.map(g => ({ label: g.name, value: g.id as number, isGroup: true }))
    ];
  }

  get hasGroupFilter(): boolean {
    return this.groupFilterControl.value !== 'all';
  }

  get activeGroupFilterLabel(): string {
    const v = this.groupFilterControl.value;
    if (v === 'groups') return this.translate.instant('speciality_group_filter_groups');
    if (v === 'ungrouped') return this.translate.instant('speciality_group_filter_ungrouped');
    return this.groups.find(g => g.id === v)?.name ?? '';
  }

  ngOnInit() {
    this.templates = {
      'name': this.nameTpl,
      'group': this.groupTpl,
      'actions': this.actionsTpl,
      'visibility': this.statusTpl
    };

    this.store.select(selectBranchId).subscribe(id => {
      this.branchId = id;
      if (id) {
        this.loadGroups();
        this.loadData();
      }
    });

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.first = 0;
      this.loadData();
    });

    this.groupFilterControl.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.first = 0;
      this.loadData();
    });
  }

  async loadGroups() {
    if (!this.branchId) return;
    try {
      this.groups = await firstValueFrom(this.settingsService.getSpecialityGroups(this.branchId));
      // A filtered group that no longer exists (deleted) falls back to "all".
      const current = this.groupFilterControl.value;
      if (typeof current === 'number' && !this.groups.some(g => g.id === current)) {
        this.groupFilterControl.setValue('all', { emitEvent: false });
      }
      this.cdr.detectChanges();
    } catch (err) {
      console.error(err);
    }
  }

  async loadData() {
    if (!this.branchId) return;
    this.loading = true;
    const search = this.searchControl.value || '';
    const filter = this.groupFilterControl.value;
    try {
      const result = await firstValueFrom(
        this.settingsService.getSpecialties(this.branchId, search, this.rows, this.first, false, {
          includeGroups: true,
          groupFilter: filter === 'all' ? null : filter
        })
      );
      this.specialties = result.data;
      this.totalRecords = result.count || 0;

      const groupIds = this.specialties.filter(s => s.is_group).map(s => s.id as number);
      this.memberCounts = groupIds.length
        ? await firstValueFrom(this.settingsService.getSpecialityGroupMemberCounts(groupIds))
        : {};
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_loading_data'));
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    this.loadData();
  }

  clearGroupFilter() {
    this.groupFilterControl.setValue('all');
  }

  /** Clicking a group's name in the table filters the list to its members. */
  filterByGroup(groupId: number) {
    this.groupFilterControl.setValue(groupId);
  }

  onAdd() {
    if (!this.canCreate) return;
    this.selectedSpeciality = null;
    this.modalIsGroup = false;
    this.showSpecialityModal = true;
  }

  onAddGroup() {
    if (!this.canCreate) return;
    this.selectedSpeciality = null;
    this.modalIsGroup = true;
    this.showSpecialityModal = true;
  }

  onEdit(speciality: any) {
    if (!this.canEdit) return;
    this.selectedSpeciality = speciality;
    this.modalIsGroup = !!speciality.is_group;
    this.showSpecialityModal = true;
  }

  async toggleVisibility(speciality: any) {
    if (!this.canEdit) return;
    const newVisibility = !speciality.visibility;
    try {
      await firstValueFrom(this.settingsService.updateSpeciality(speciality.id, { visibility: newVisibility }));
      toast.success(this.translate.instant('speciality_visibility_updated'));
      this.loadData();
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('speciality_visibility_error'));
    }
  }

  // ----- Add to group -------------------------------------------------------

  openAssignModal(speciality: any) {
    if (!this.canEdit || speciality.is_group) return;
    this.specialityToAssign = speciality;
    this.assignGroupId = speciality.group_id ?? null;
    this.showAssignModal = true;
  }

  closeAssignModal() {
    this.showAssignModal = false;
    this.specialityToAssign = null;
    this.assignGroupId = null;
  }

  async confirmAssign() {
    if (!this.canEdit || !this.specialityToAssign) return;
    this.assigning = true;
    try {
      await firstValueFrom(
        this.settingsService.updateSpeciality(this.specialityToAssign.id, { group_id: this.assignGroupId })
      );
      toast.success(this.translate.instant('speciality_group_assigned'));
      this.closeAssignModal();
      this.loadData();
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('speciality_error_save'));
    } finally {
      this.assigning = false;
    }
  }

  // ----- Delete -------------------------------------------------------------

  openDeleteModal(speciality: any) {
    if (!this.canDelete) return;
    this.specialityToDelete = speciality;
    this.showDeleteModal = true;
  }

  async confirmDelete() {
    if (!this.canDelete) return;
    if (!this.specialityToDelete) return;
    const wasGroup = !!this.specialityToDelete.is_group;

    try {
      await firstValueFrom(this.settingsService.deleteSpeciality(this.specialityToDelete.id));
      toast.success(this.translate.instant(wasGroup ? 'speciality_group_deleted' : 'speciality_deleted'));
      if (wasGroup) await this.loadGroups();
      this.loadData();
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('settings_user_error_delete'));
    } finally {
      this.showDeleteModal = false;
      this.specialityToDelete = null;
    }
  }

  closeModal() {
    this.showSpecialityModal = false;
    this.selectedSpeciality = null;
  }

  async onSaved() {
    if (this.modalIsGroup) await this.loadGroups();
    this.loadData();
    this.closeModal();
  }
}
